import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { TextZoom } from '@capacitor/text-zoom';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import * as icons from 'ionicons/icons';
import { Subject, takeUntil } from 'rxjs';
import { AsisstantSubscriptionPlanRecommendationModalComponent } from '@components/assistant/subscription/plan-recommendation/modal/modal.component';
import { AuthComponent } from '@components/auth/auth.component';
import { BackComponent } from '@components/back/back.component';
import { DeeplinksComponent } from '@components/deeplinks/deeplinks.component';
import { HeartbeatComponent } from '@components/heartbeat/heartbeat.component';
import { KeyboardComponent } from '@components/keyboard/keyboard.component';
import { LangComponent } from '@components/lang/lang.component';
import { NetworkComponent } from '@components/network/network.component';
import { OneSignalComponent } from '@components/one-signal/one-signal.component';
import { ScreenOrientationComponent } from '@components/screen-orientation/screen-orientation.component';
import { SentryComponent } from '@components/sentry/sentry.component';
import { StatusBarComponent } from '@components/status-bar/status-bar.component';
import { SyncDeviceTimezoneComponent } from '@components/sync-device-timezone/sync-device-timezone.component';
import { ToastComponent } from '@components/toast/toast.component';
import { UpdateComponent } from '@components/update/update.component';
import { ChangeAppBackgroundColorOptions } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { ACLComponent } from '../components/acl/acl.component';
import { ClipboardComponent } from '../components/clipboard/clipboard.component';
import { OverlayComponent } from '../components/overlay/overlay.component';
import { ReminderComponent } from '../components/reminder/reminder.component';
import { TrackerSyncComponent } from './tracker/sync/sync.component';

/**
 * App Component.
 *
 * @exports
 * @class AppComponent
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    IonRouterOutlet,
    ACLComponent,
    AuthComponent,
    BackComponent,
    ClipboardComponent,
    DeeplinksComponent,
    HeartbeatComponent,
    KeyboardComponent,
    LangComponent,
    NetworkComponent,
    OneSignalComponent,
    OverlayComponent,
    ReminderComponent,
    ScreenOrientationComponent,
    SentryComponent,
    StatusBarComponent,
    ToastComponent,
    TrackerSyncComponent,
    UpdateComponent,
    AsisstantSubscriptionPlanRecommendationModalComponent,
    SyncDeviceTimezoneComponent
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly event = inject(EventService);

  // Get IonRouterOutlet reference for back component
  // eslint-disable-next-line kawaī/viewchild-private-readonly
  protected readonly iro = viewChild.required(IonRouterOutlet);

  appBgColors = {
    default: 'white',
    prodigyPage: '#141414',
    takePicture: 'transparent'
  };
  readonly appBgColor = signal<string>(this.appBgColors.default);
  hideMainElement = false;
  destroyed = new Subject<boolean>();

  /**
   * Constructor.
   */
  constructor() {
    addIcons({ ...icons });
  }

  /**
   * @description Init.
   * @memberof AppComponent
   */
  ngOnInit(): void {
    this.init();
  }

  /**
   * Setup.
   */
  ngAfterViewInit(): void {
    this.subscribe();
  }

  /**
   * Cleanup.
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  /**
   * Subscribe to events.
   */
  subscribe(): void {
    this.event
      .listen(this.event.keys.HIDE_ROOT_ELEMENT)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set value of hideMainElement.
         *
         * @param {boolean} show Show.
         */
        next: (show: boolean) => {
          this.hideMainElement = show;
        }
      });

    this.event
      .listen(this.event.keys.CHANGE_APP_BACKGROUND_COLOR)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set value of hideMainElement.
         *
         * @param {boolean} value Value.
         */
        next: (value: ChangeAppBackgroundColorOptions) => {
          if (value === ChangeAppBackgroundColorOptions.PRODIGY_PAGE) {
            this.appBgColor.set(this.appBgColors.prodigyPage);
          } else if (value === ChangeAppBackgroundColorOptions.TAKE_PICTURE) {
            this.appBgColor.set(this.appBgColors.takePicture);
          } else {
            this.appBgColor.set(this.appBgColors.default);
          }
        }
      });
  }

  /**
   * Initialize native plugins.
   *
   * @returns {Promise<void>}
   */
  async init(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        // Set text zoom to 100%
        await TextZoom.set({ value: 1 });

        if (Capacitor.getPlatform() === 'android') {
          // Set status bar and navigation bar colors
          await EdgeToEdge.setStatusBarColor({ color: '#ffffff' });
          await EdgeToEdge.setNavigationBarColor({ color: '#ffffff' });
          await EdgeToEdge.enable();
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Set status bar style
          await StatusBar.setOverlaysWebView({ overlay: true });
          await StatusBar.setBackgroundColor({ color: '#ffffffff' });
          await StatusBar.setStyle({ style: Style.Light });
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}
